import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Cropper from 'react-easy-crop';
import { ModalBase } from '@components/Modal';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { validateFileSize } from '@helpers/upload-file';

const ImageCropModal = ({
    open,
    imageSrc,
    aspectRatio,
    outputWidth,
    outputMinWidth,
    outputMaxWidth,
    maxSize,
    minSize,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleSave = () => {
        if (!croppedAreaPixels || !imageSrc) return;

        const img = new Image();
        img.src = imageSrc;
        img.onload = () => {
            const { x, y, width: cropW, height: cropH } = croppedAreaPixels;

            let outWidth;
            if (outputWidth !== undefined) {
                outWidth = outputWidth;
            } else {
                const minW = outputMinWidth ?? 0;
                const maxW = outputMaxWidth ?? Infinity;
                outWidth = Math.min(Math.max(cropW, minW), maxW);
            }
            const outHeight = Math.round(outWidth / aspectRatio);

            const exportAtScale = scale => {
                const w = Math.round(outWidth * scale);
                const h = Math.round(outHeight * scale);
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, x, y, cropW, cropH, 0, 0, w, h);

                canvas.toBlob(blob => {
                    if (!blob) return;
                    if (minSize && blob.size < minSize && scale < 4) {
                        exportAtScale(scale * 2);
                        return;
                    }
                    const { error, metric, size } = validateFileSize(blob, maxSize, minSize);
                    if (error) {
                        dispatch(alertActions.error(t(`apiErrors.${error}`, { size, metric })));
                        return;
                    }
                    onSave(new File([blob], 'image.png', { type: 'image/png' }));
                }, 'image/png');
            };

            exportAtScale(1);
        };
    };

    return (
        <ModalBase
            isOpen={open}
            handleClose={onClose}
            title={t('buttons.cropImage')}
            onPrimaryAction={handleSave}
            primaryActionText={t('buttons.save')}
        >
            <div style={{ position: 'relative', width: 500, height: 500 }}>
                {imageSrc && (
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspectRatio}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                )}
            </div>
        </ModalBase>
    );
};

export default ImageCropModal;
